import { IOnePageInfo } from "@/types/types";
import React, { useEffect, useState } from "react";
import { Outlet, useParams, useLocation } from "react-router";
import { useSmartNavigate } from "@/hooks";
import { Splitter, Spin, Result, Button } from "antd";
import { FileSearchOutlined } from "@ant-design/icons";
import { useAppRouteContext } from "@/contexts";
import { getMenus } from "@/services";
import {
  findMenuTreeNode,
  isLowcodePage,
  getEffectivePagePath,
  getAllSearchParams,
  unzipurl,
} from "@/utils";
import { OnePageActionBar } from "@/components";
import {
  LowcodePage,
  FormResultPage,
  FormEditPage,
  FormDetailPage,
} from "@/pages";
import styles from "./index.module.css";

// onePage内容渲染组件
const RenderOnePage = ({ url, basePath, parentPath, currentPath }) => {
  const { setOnePageBasePath } = useAppRouteContext();
  const [menuInfo, setMenuInfo] = useState(null);
  const urlString = `/${url}`; // 补齐url前缀 /

  // 构造完整url对象 (这里域名没用)
  const { pathname, search, hash } = new URL(urlString, "https://example.com");
  // 获取有效的能匹配到低码页面的path, (忽略掉最后的 /result /update /detail /)
  const pagePath = getEffectivePagePath(pathname);
  // search string -> 转对象
  const searchParams = getAllSearchParams(search);

  const onePageInfo: IOnePageInfo = {
    basePath, // 当前onePage页面所处的根页面path（前缀带 / ）
    parentPath, // 当前onePage页面的父级path（前缀带 / ）
    currentPath, // 当前onePage页面的完整path（前缀带 / ）

    innerHref: urlString, // onePage页面完整url（前缀带 / ）
    innerPagePath: pagePath, // onePage页面能够匹配到低码页面的path（前缀带 / ）
    innerSearchParams: searchParams, // onePage页面的params对象
  };

  const navigate = useSmartNavigate(onePageInfo);

  useEffect(() => {
    const getMenuInfo = async () => {
      const { menus } = await getMenus();
      const node = findMenuTreeNode(menus, pagePath); // 找到这个path对应菜单项的详细信息
      setMenuInfo(node || {});
    };
    getMenuInfo();
  }, [urlString]);

  // basePath 变化时, 设置全局 onePageBasePath, 用于处理菜单选中项
  useEffect(() => {
    setOnePageBasePath(basePath);
    return () => {
      setOnePageBasePath("");
    };
  }, [basePath]);

  // TODO: 当前仅支持低码页面
  if (isLowcodePage(menuInfo?.type)) {
    let children = null;
    if (menuInfo?.pageType === "CREATE" && pathname.endsWith("/result")) {
      children = <FormResultPage onePageInfo={onePageInfo} />;
    } else if (
      menuInfo?.pageType === "CREATE" &&
      pathname.endsWith("/update")
    ) {
      children = <FormEditPage onePageInfo={onePageInfo} />;
    } else if (
      menuInfo?.pageType === "CREATE" &&
      pathname.endsWith("/detail")
    ) {
      children = <FormDetailPage onePageInfo={onePageInfo} />;
    } else {
      children = <LowcodePage onePageInfo={onePageInfo} />;
    }
    return (
      <>
        <div className={styles.onePageActionBar}>
          <OnePageActionBar onePageInfo={onePageInfo} />
        </div>
        {children}
      </>
    );
  }

  // 页面没有找到的情况
  if (menuInfo !== null) {
    return (
      <div className={styles.onePageNotFound}>
        <Result
          icon={<FileSearchOutlined style={{ fontSize: "3em" }} />}
          // title="页面未找到"
          subTitle={`页面未找到：${urlString}`}
          extra={
            <Button size="small" onClick={() => navigate("../")}>
              关闭
            </Button>
          }
        />
      </div>
    );
  }

  // loading 状态
  return (
    <div className={styles.onePageNotFound}>
      <Spin />
    </div>
  );
};

// onePage容器组件
const OnePageContainer = () => {
  const { "*": stackPath } = useParams();
  const { pathname } = useLocation();
  const basePath = stackPath ? pathname.replace(`/${stackPath}`, "") : pathname;
  let parentPath = basePath;
  const stack = stackPath?.split("/").map((path) => {
    let unzipPath;
    try {
      // 如果传入的path没有经过压缩, unzipurl会返回空或者报错, 在这里统一处理
      unzipPath = unzipurl(path) || path;
    } catch (error) {
      unzipPath = path;
    }
    const currentPath = parentPath + "/" + path;
    const obj = {
      url: unzipPath,
      parentPath,
      currentPath,
    };
    parentPath = currentPath;
    return obj;
  });

  return (
    <Splitter className={stack ? styles.isOnePage : ""}>
      <Splitter.Panel className={styles.mainPanel} defaultSize="65%">
        <Outlet />
      </Splitter.Panel>
      {stack?.map(({ url, parentPath, currentPath }) => {
        return (
          <Splitter.Panel
            key={url}
            className={`layout-one-page-panel-container ${styles.onePagePanel}`}
          >
            <RenderOnePage
              key={url}
              url={url}
              basePath={basePath}
              parentPath={parentPath}
              currentPath={currentPath}
            />
          </Splitter.Panel>
        );
      })}
    </Splitter>
  );
};

export default OnePageContainer;
